import { HydratedRouter } from 'react-router/dom'
import {
  browserTracingIntegration,
  init,
  replayIntegration
} from '@sentry/react-router'
import { startTransition, StrictMode } from 'react'
import { hydrateRoot } from 'react-dom/client'

import { filterThirdPartyErrors } from './utils/sentry-error-filters'

const env = {
  APP_ENV: import.meta.env.VITE_APP_ENV,
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN
}

// Only initialize Sentry in non-dev environments
if (env.APP_ENV !== 'dev') {
  init({
    dsn: env.SENTRY_DSN,
    tracesSampleRate: 1,
    enableLogs: env.APP_ENV === 'stg',
    debug: env.APP_ENV === 'stg',
    environment: env.APP_ENV,
    integrations: [
      browserTracingIntegration(),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      })
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    beforeSend(event, hint) {
      // Filter out benign React Router view transition errors
      if (
        event.exception?.values?.[0]?.type === 'AbortError' &&
        event.exception.values[0].value?.includes(
          'Skipping view transition because skipTransition() was called'
        )
      ) {
        return null
      }

      // Filter out third-party script and theme errors
      const filteredEvent = filterThirdPartyErrors(
        event as Parameters<typeof filterThirdPartyErrors>[0]
      )
      if (filteredEvent === null) {
        return null
      }

      // Tag CORS errors for easier filtering in Sentry (but still report them)
      if (event.exception?.values?.[0]?.type === 'Error') {
        const errorMessage = event.exception.values[0].value || ''
        const originalError = hint?.originalException as Error | undefined

        // Check if this is a CORS-related error
        const isCorsError =
          errorMessage.includes('CORS') ||
          errorMessage.includes('access control') ||
          (errorMessage.includes('Failed to fetch') &&
            originalError?.message?.includes('CORS'))

        if (isCorsError) {
          // Tag CORS errors so they can be filtered/grouped in Sentry
          event.tags = {
            ...event.tags,
            error_type: 'cors',
            error_category: 'configuration'
          }
          event.contexts = {
            ...event.contexts,
            cors: {
              message: errorMessage,
              url: event.request?.url
            }
          }
        }
      }

      return event
    }
  })
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter />
    </StrictMode>
  )
})
